import React, { useEffect, useState } from "react";
import {
  Box,
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useAuth } from "context/AuthContext";
import { usePageTitle } from "context/PageTitleContext";
import { loadAuthorization } from "auth/authorization.service";
import { getToken } from "services/auth.service";
import PermissionsTab from "../components/profile/PermissionsTab";
import PersonalInfoTab from "../components/profile/PersonalInfoTab";
import SecurityTab from "../components/profile/SecurityTab";
import { AppTab, AppTabPanel } from "components/common/tabs";
import "./UserProfile.css";

const profileTabs = ["Thông tin cá nhân", "Bảo mật", "Quyền hạn"];

const UserProfile = () => {
  const { user } = useAuth();
  const { setPageTitle } = usePageTitle();
  const [currentTab, setCurrentTab] = useState(0);
  const [userStats, setUserStats] = useState({
    totalContracts: 0,
    totalPermissions: 0,
    totalApplications: 0,
    totalRoles: 0,
    accountCreated: null,
    lastLogin: null,
  });
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState("");

  useEffect(() => {
    setPageTitle("Thông tin tài khoản");
    loadUserStats();
  }, [setPageTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserStats = async () => {
    try {
      setLoading(true);
      setStatsError("");

      if (!user?.userId) {
        setLoading(false);
        return;
      }

      const token = getToken();
      let totalContracts = 0;

      try {
        const contractCountResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/User/${user.userId}/contracts/count`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (contractCountResponse.ok) {
          const countData = await contractCountResponse.json();
          if (countData.success && countData.data) {
            totalContracts = countData.data.contractCount || 0;
          }
        } else {
          throw new Error("Không thể tải số lượng hợp đồng.");
        }
      } catch (error) {
        totalContracts = null;
        setStatsError("Chưa tải đủ thống kê phạm vi truy cập. Hãy tải lại trang.");
      }

      const authorization = await loadAuthorization();

      setUserStats({
        totalContracts,
        totalPermissions: authorization?.permissions?.length || 0,
        totalApplications: authorization?.applications?.length || 0,
        totalRoles: authorization?.roles?.length || 0,
        accountCreated: null,
        lastLogin: null,
      });
    } catch (error) {
      setStatsError("Không thể tải thống kê phạm vi truy cập. Hãy tải lại trang.");
      console.error("Error loading user stats:", error);
      setUserStats({
        totalContracts: null,
        totalPermissions: null,
        totalApplications: null,
        totalRoles: null,
        accountCreated: null,
        lastLogin: null,
      });
    } finally {
      setLoading(false);
    }
  };

  const role = user?.roleName || user?.role;
  const getRoleBadgeColor = (roleName) => {
    const colors = {
      Admin: "#7c3aed",
      Operator: "#2563eb",
      Creator: "#16a34a",
      Viewer: "#64748b",
    };
    return colors[roleName] || "#64748b";
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="user-profile-container">
      <Paper className="user-profile-content" elevation={0}>
        <Box className="user-profile-heading">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" className="user-profile-name">
              {user?.fullName || user?.userName}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user?.email || user?.userName}
            </Typography>
          </Box>
          <Chip
            label={role || "Vai trò chưa xác định"}
            size="small"
            sx={{
              flexShrink: 0,
              backgroundColor: getRoleBadgeColor(role),
              color: "#fff",
              fontWeight: 600,
            }}
          />
        </Box>

        <AppTab
          value={currentTab}
          onChange={(_, nextTab) => setCurrentTab(nextTab)}
          tabs={profileTabs}
          variant="scrollable"
          scrollButtons="auto"
          className="user-profile-tabs"
        />
      </Paper>

      <Box className="user-profile-tab-content">
        <AppTabPanel
          value={currentTab}
          index={0}
          sx={{ display: "grid", gap: 2.5 }}
        >
          <>
            <PersonalInfoTab user={user} onUserUpdate={loadUserStats} />

            <Card variant="outlined" className="user-profile-overview-card">
              <CardContent className="user-profile-overview-content">
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>
                  Tổng quan phạm vi truy cập
                </Typography>
                {statsError && <Alert severity="warning">{statsError}</Alert>}

                {loading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box className="user-profile-stat-item">
                        <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                          {userStats.totalRoles ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Vai trò được gán
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box className="user-profile-stat-item">
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                          {userStats.totalApplications ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ứng dụng được truy cập
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box className="user-profile-stat-item">
                        <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 700 }}>
                          {userStats.totalPermissions ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Quyền hạn hiệu lực
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box className="user-profile-stat-item">
                        <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                          {userStats.totalContracts ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Hợp đồng được truy cập
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </>
        </AppTabPanel>
        <AppTabPanel value={currentTab} index={1}>
          <SecurityTab user={user} />
        </AppTabPanel>
        <AppTabPanel value={currentTab} index={2}>
          <PermissionsTab user={user} />
        </AppTabPanel>
      </Box>
    </Box>
  );
};

export default UserProfile;
